import { TrackClick } from '@carvana/merch-components';
import cx from 'classnames';
import { ChevronRight } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import Section from '~/constants/vdp/section';
import SubSections from '~/constants/vdp/subSections';
import { useSectionAnalytics } from '~/context/vdp/SectionAnalyticsContext';
import useScrollToSection from '~/hooks/vdp/useScrollToSection';

type Props = {
  adobeId: string | null;
  analyticsDescription: string;
  bgColor: string;
  compactLeadingText?: string;
  compactText?: string;
  leadingText: string;
  onSashClick?: () => void;
  section: Section | SubSections;
  testId: string;
  text?: string;
};

export default function InfoSash({
  adobeId,
  analyticsDescription,
  bgColor,
  compactLeadingText,
  compactText,
  leadingText,
  onSashClick,
  section,
  testId,
  text
}: Props) {
  const scrollToSection = useScrollToSection();
  const { analyticsAttributes, analyticsId } = useSectionAnalytics();
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  const hasCompactVariant = Boolean(compactLeadingText || compactText);

  const sashRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || !hasCompactVariant) return;

      const measureEl = measureRef.current;
      if (measureEl) {
        measureEl.textContent = `${leadingText}: ${text}`;
      }

      observerRef.current = new ResizeObserver(() => {
        if (!measureEl) return;

        const style = getComputedStyle(measureEl);
        const lineHeight =
          parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;

        setIsCompact(measureEl.offsetHeight > lineHeight * 1.5);
      });
      observerRef.current.observe(node);
    },
    [hasCompactVariant, leadingText, text]
  );

  const displayLeadingText =
    isCompact && compactLeadingText ? compactLeadingText : leadingText;
  const displayText = isCompact && compactText ? compactText : text;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      scrollToSection(section);
      onSashClick?.();
    },
    [onSashClick, scrollToSection, section]
  );

  return (
    <TrackClick
      className="w-full"
      data-adobe-id={adobeId}
      data-analytics-attributes={{
        ...analyticsAttributes,
        description: analyticsDescription,
        text
      }}
      data-analytics-id={analyticsId}
      data-testid={testId}
      element="a"
      href={`#${section}`}
      onClick={handleClick}
    >
      <div
        ref={sashRef}
        className={cx(
          bgColor,
          'flex w-full items-center justify-between gap-sm rounded-sm px-md py-[3px] text-text-strong'
        )}
      >
        <div className="relative min-w-0 flex-1">
          {hasCompactVariant && (
            <div
              ref={measureRef}
              className="pointer-events-none invisible absolute inset-x-0 top-0"
            />
          )}
          <div className="flex flex-wrap gap-x-xs">
            <span className="t-label-xs">{displayLeadingText}</span>
            {displayText && <span className="t-body-xs">{displayText}</span>}
          </div>
        </div>
        <ChevronRight size={16} />
      </div>
    </TrackClick>
  );
}
